/**
 * The buttons on a session row — ONE next step, by where the class stands.
 *
 * Brian, 22 Sep: "Run / Present / Close is a little funny on the main page."
 * It was: every row offered the same handful, Present and Close sat on the
 * list beside Run it, and Close on a list skipped the roster screen where the
 * room is verified first. Now a row offers what comes next in the run of show
 * — open → run it → certify → award → the packet — and Present and Close live
 * inside Run it, which is where the instructor is standing when they need them.
 *
 * Shared by the Sessions board, a section's Sessions panel and Home, so the
 * three places say the same thing about the same class.
 */
import { Link } from 'react-router-dom';

/** Where a session is, in words, and what the primary step is. */
export function sessionStage(s) {
  if (s.status === 'scheduled') return { key: 'scheduled', label: 'Scheduled' };
  if (s.status === 'active') return { key: 'active', label: 'Open now' };
  if (s.status === 'canceled') return { key: 'canceled', label: 'Cancelled' };
  if (s.status === 'closed') {
    if (s.is_impromptu && s.class_version == null) return { key: 'bind', label: 'Closed · needs a section' };
    const p = s.progress ?? {};
    if (p.certified === false) return { key: 'certify', label: 'Closed · needs certifying' };
    if (p.awarded === false) return { key: 'award', label: 'Closed · needs awarding' };
    if (p.certified && p.awarded) return { key: 'done', label: 'Complete' };
    return { key: 'closed', label: 'Closed' };
  }
  return { key: s.status, label: s.status };
}

export function SessionStageBadge({ session }) {
  const stage = sessionStage(session);
  const cls =
    stage.key === 'active'
      ? 'badge badge--published'
      : stage.key === 'certify' || stage.key === 'award' || stage.key === 'bind'
        ? 'badge badge--warn'
        : 'badge badge--archived';
  return <span className={cls}>{stage.label}</span>;
}

/**
 * @param session    a row from GET /sessions (with `progress` on closed rows)
 * @param onOpen     opens a scheduled session; omit to hide the button
 * @param busy       disables the buttons while something is in flight
 * @param compact    Home's narrow list: the primary step only
 * @param children   extra buttons the caller owns (Change time, Take off the board)
 */
export function SessionNextStep({ session: s, onOpen, busy = false, compact = false, children = null }) {
  const stage = sessionStage(s);
  const roster = `/sessions/${s.id}/roster`;
  const bound = s.class_version != null;

  let primary = null;
  const quiet = [];

  switch (stage.key) {
    case 'scheduled':
      primary = onOpen ? (
        <button type="button" className="btn btn--primary btn--auto" disabled={busy} onClick={() => onOpen(s.id)}>
          Open the class
        </button>
      ) : (
        <Link className="btn btn--auto" to={roster}>Roster</Link>
      );
      if (onOpen) quiet.push(<Link key="roster" className="btn btn--quiet btn--auto" to={roster}>Roster</Link>);
      break;
    case 'active':
      // Present ▶ and Close are on the roster screen, where the class is run.
      primary = <Link className="btn btn--primary btn--auto" to={roster}>Run it</Link>;
      break;
    case 'bind':
      primary = <Link className="btn btn--primary btn--auto" to={roster}>Bind it to a section</Link>;
      break;
    case 'certify':
      primary = <Link className="btn btn--primary btn--auto" to={`${roster}#close-out`}>Certify</Link>;
      break;
    case 'award':
      primary = <Link className="btn btn--primary btn--auto" to={`${roster}#close-out`}>Award completions</Link>;
      break;
    case 'canceled':
      quiet.push(<Link key="roster" className="btn btn--quiet btn--auto" to={roster}>Roster</Link>);
      break;
    default:
      // Complete (or closed with progress unknown to this account): the paperwork.
      if (bound) {
        primary = <Link className="btn btn--primary btn--auto" to={`/sections/${s.class_id}/packet`}>Closing packet</Link>;
        quiet.push(<Link key="record" className="btn btn--quiet btn--auto" to={`/sections/${s.class_id}/record`}>Attendance record</Link>);
      }
      quiet.push(<Link key="roster" className="btn btn--quiet btn--auto" to={roster}>Roster</Link>);
  }

  return (
    <div className="schedule__actions">
      {primary}
      {!compact && quiet}
      {!compact && children}
    </div>
  );
}

export default SessionNextStep;
